/*
  Popup Lista VIP + banner de expiración.
  - El deadline llega en data-deadline como "AAAA-MM-DD HH:MM" en hora de Perú
    (UTC-5 fijo, sin horario de verano) y debe coincidir con la fecha de fin
    REAL del código de descuento en Shopify.
  - Estado en el navegador: cookie 'vip-popup-closed' (no re-molestar),
    localStorage 'vip-subscribed' (habilita el banner y apaga popup/teaser)
    y 'vip-subscribed-at' (momento exacto de suscripción, usado para el
    deadline personal de 7 días — ver vipPersonalDeadline).
  - dataLayer (GTM ya está instalado en layout/theme.liquid): 'open_popup' al
    mostrarse el formulario (por delay o al tocar la burbuja), 'subscriber_popup'
    al mostrarse el modal de éxito tras suscribirse. No se disparan en el editor
    de temas (Shopify.designMode).
*/

function vipPushEvent(event) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event });
}

function vipParseDeadline(raw) {
  if (!raw) return NaN;
  return Date.parse(raw.trim().replace(' ', 'T') + ':00-05:00');
}

/*
  Deadline personal de urgencia: 7 días desde que ESTA clienta se suscribió.
  El código VIRAGO15 es evergreen (nunca expira de verdad en Shopify) — esto
  es framing de marketing, no una fecha que el checkout haga cumplir. Se fija
  una sola vez, al suscribirse, y queda en localStorage.
*/
const VIP_URGENCY_DAYS = 7;

function vipPersonalDeadline() {
  let subscribedAt = localStorage.getItem('vip-subscribed-at');
  if (!subscribedAt) {
    subscribedAt = String(Date.now());
    localStorage.setItem('vip-subscribed-at', subscribedAt);
  }
  return Number(subscribedAt) + VIP_URGENCY_DAYS * 86400000;
}

function vipFormatRemaining(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = n => String(n).padStart(2, '0');
  if (days > 0) return `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

function vipStartCountdown(root, deadline, onExpire) {
  const target = root.querySelector('[data-vip-countdown]');
  if (!target || Number.isNaN(deadline)) return;
  const tick = () => {
    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      clearInterval(interval);
      onExpire();
      return;
    }
    target.textContent = vipFormatRemaining(remaining);
  };
  const interval = setInterval(tick, 1000);
  tick();
}

/*
  Gate de UTM: la visita "de campaña" se detecta en la URL de aterrizaje y se
  persiste en sessionStorage — al navegar, las páginas siguientes ya no llevan
  los utm_ pero la visitante sigue siendo tráfico de campaña. Con filtro, solo
  cuentan los utm_source de la lista.
*/
function vipCheckUtm(filterRaw) {
  const params = new URLSearchParams(window.location.search);
  const source = (params.get('utm_source') || '').toLowerCase();
  const hasUtm = [...params.keys()].some(k => k.startsWith('utm_'));
  const filter = (filterRaw || '')
    .split(',')
    .map(s => s.trim().toLowerCase())
    .filter(Boolean);

  if (hasUtm && (filter.length === 0 || filter.includes(source))) {
    sessionStorage.setItem('vip-utm', 'true');
  }
  return sessionStorage.getItem('vip-utm') !== null;
}

class VipPopup extends ModalDialog {
  constructor() {
    super();
    this.delay = (parseInt(this.dataset.delay, 10) || 6) * 1000;
    this.deadline = vipParseDeadline(this.dataset.deadline);
    this.isSuccess = this.dataset.success === 'true';
    this.form = this.querySelector('.js-vip-form');
    this.teaser = document.querySelector('[data-vip-teaser]');
    this.teaser?.addEventListener('click', () => {
      vipPushEvent('open_popup');
      this.show();
    });

    const copyButton = this.querySelector('[data-vip-copy]');
    if (copyButton) {
      copyButton.addEventListener('click', () => {
        const code = this.querySelector('.vip-popup__code-value');
        if (!code || !navigator.clipboard) return;
        navigator.clipboard.writeText(code.textContent.trim()).then(() => {
          const copyIcon = copyButton.querySelector('.vip-popup__icon-copy');
          const checkIcon = copyButton.querySelector('.vip-popup__icon-check');
          copyIcon.hidden = true;
          checkIcon.hidden = false;
          setTimeout(() => {
            copyIcon.hidden = false;
            checkIcon.hidden = true;
          }, 2000);
        });
      });
    }
  }

  connectedCallback() {
    if (Shopify.designMode) {
      if (this.dataset.openInDesignMode === 'true') this.show();
      return;
    }

    if (this.isSuccess) {
      localStorage.setItem('vip-subscribed', String(Date.now()));
      this.personalDeadline = vipPersonalDeadline();
      this.teaser?.remove();
      this.setupSuccess();
      this.show();
      vipPushEvent('subscriber_popup');
      document.dispatchEvent(new CustomEvent('vip:subscribed'));
      return;
    }

    const closed = getCookie('vip-popup-closed');
    const subscribed = localStorage.getItem('vip-subscribed');
    const expired = !Number.isNaN(this.deadline) && Date.now() > this.deadline;
    if (subscribed !== null || expired) return;

    if (this.dataset.utmOnly === 'true' && !vipCheckUtm(this.dataset.utmFilter)) return;

    if (closed !== null) {
      // Cerró el popup en una visita anterior: queda la burbuja, no el modal.
      if (this.teaser) this.teaser.hidden = false;
      return;
    }

    setTimeout(() => {
      vipPushEvent('open_popup');
      this.show();
    }, this.delay);
  }

  setupSuccess() {
    const countdownRow = this.querySelector('[data-vip-countdown-row]');
    if (countdownRow) {
      countdownRow.hidden = false;
      vipStartCountdown(this, this.personalDeadline, () => {
        countdownRow.hidden = true;
      });
    }

    const expiryEl = this.querySelector('.vip-popup__expiry');
    if (expiryEl && !Number.isNaN(this.personalDeadline)) {
      const formatted = new Date(this.personalDeadline).toLocaleDateString('es-PE', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: 'America/Lima',
      });
      expiryEl.innerHTML = `Tu cupón vence el <strong>${formatted}</strong>`;
    }
  }

  show(opener) {
    super.show(opener);
    if (this.teaser) this.teaser.hidden = true;
  }

  hide() {
    super.hide();
    setCookie('vip-popup-closed', 'true');
    if (this.teaser && !this.isSuccess) this.teaser.hidden = false;
  }
}

customElements.define('vip-popup', VipPopup);

class VipBanner extends HTMLElement {
  connectedCallback() {
    if (Shopify.designMode) return;
    this.deadline = vipPersonalDeadline();
    this.querySelector('[data-vip-banner-close]')?.addEventListener('click', () => {
      localStorage.setItem('vip-banner-dismissed', 'true');
      this.hidden = true;
      this.reserveSpace();
    });
    document.addEventListener('vip:subscribed', () => this.evaluate());
    this.evaluate();
  }

  evaluate() {
    const subscribed = localStorage.getItem('vip-subscribed');
    const dismissed = localStorage.getItem('vip-banner-dismissed');
    const expired = Number.isNaN(this.deadline) || Date.now() > this.deadline;
    if (!subscribed || dismissed || expired) return;
    this.hidden = false;
    this.reserveSpace();
    window.addEventListener('resize', () => this.reserveSpace());
    vipStartCountdown(this, this.deadline, () => {
      this.hidden = true;
      this.reserveSpace();
    });
  }

  /* Empuja la página hacia abajo el alto del banner para no tapar el
     announcement bar ni el menú (el banner es position:fixed). */
  reserveSpace() {
    document.body.style.paddingTop = this.hidden ? '' : `${this.offsetHeight}px`;
  }
}

customElements.define('vip-banner', VipBanner);
